import { Flex, Text } from '@radix-ui/themes'
import type { TooltipProps } from 'recharts'
import { SERIES } from './chartStyle'
import { formatBucketTime, formatLitres, formatNullableLitres } from './format'
import { bandKindAt, type LeakV0ChartRow, type LeakV0StateBand } from './chartModel'

interface LeakV0ChartTooltipProps extends TooltipProps<number, string> {
  rowsByTime: Map<number, LeakV0ChartRow>
  bands: LeakV0StateBand[]
  shaypAvailable: boolean
}

interface TooltipLine {
  label: string
  value: string
  color?: string
}

const toLines = (row: LeakV0ChartRow, shaypAvailable: boolean, state: string): TooltipLine[] => [
  { ...SERIES.consumption, value: formatNullableLitres(row.consumptionL) },
  { ...SERIES.willieLeak, value: formatLitres(row.leakL) },
  ...(shaypAvailable ? [{ ...SERIES.shaypLeak, value: formatNullableLitres(row.shaypLeakL) }] : []),
  { label: 'Episode state', value: state }
]

export const LeakV0ChartTooltip = ({
  active,
  label,
  rowsByTime,
  bands,
  shaypAvailable
}: LeakV0ChartTooltipProps) => {
  // `label` is the time-scaled axis value, so it is the bucket's epoch millisecond key.
  const row = active && typeof label === 'number' ? rowsByTime.get(label) : undefined
  if (!row) return null

  return (
    <div className="rounded-lg border border-[var(--gray-4)] bg-[var(--color-background)] px-3 py-2 shadow-md">
      <Text size="1" weight="medium" className="mb-1.5 block text-[var(--gray-10)]">
        {formatBucketTime(row.atMs)}
      </Text>
      <Flex direction="column" gap="1">
        {toLines(row, shaypAvailable, bandKindAt(bands, row.atMs) ?? 'OFF').map((line) => (
          <Flex key={line.label} align="center" justify="between" gap="4">
            <Flex align="center" gap="2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: line.color ?? 'transparent' }}
              />
              <Text size="1" className="text-[var(--gray-11)]">
                {line.label}
              </Text>
            </Flex>
            <Text size="1" weight="medium" className="tabular-nums text-[var(--gray-12)]">
              {line.value}
            </Text>
          </Flex>
        ))}
      </Flex>
      <Text size="1" className="mt-1.5 block text-[var(--gray-9)]">
        Click to pin the bucket detail
      </Text>
    </div>
  )
}
